import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Paper, Chip, Button, IconButton,
  TextField, FormControl, InputLabel, Select, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, CircularProgress, Tooltip
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import { get, authPost, authPut, authDelete } from '../../services/apiService';
import { isAdmin, isAuthenticated } from '../../services/authService';
import { format } from 'date-fns';

const PRIMARY = '#1A2E22';
const PRIMARY_DARK = '#0F1A14';
const MUTED = '#666666';
const SURFACE = '#F8F9FA';

const PILLAR_OPTIONS = [
  '', 'Core Platform & Runtime Health', 'Third-Party Integration Reliability',
  'Safe Delivery'
];

const CATEGORY_COLORS = {
  General: { bg: '#f3f4f6', color: '#374151' },
  RFC:     { bg: '#ede9fe', color: '#5b21b6' },
  ADR:     { bg: '#dbeafe', color: '#1d4ed8' },
  Runbook: { bg: '#d1fae5', color: '#065f46' },
  Design:  { bg: '#fef3c7', color: '#92400e' }
};

const EMPTY_FORM = { title: '', externalLink: '', category: 'General', pillar: '', comments: '' };

const inputSx = {
  '& .MuiOutlinedInput-root': { borderRadius: '10px' },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: PRIMARY }
};

const ReliabilityDocumentation = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [docs, setDocs] = useState([]);
  const [filterPillar, setFilterPillar] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await get('/api/reliability/documentation');
      if (res.success) setDocs(res.data || []);
      else setError('Failed to load documentation');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setDialogOpen(true); };
  const openEdit = (d) => {
    setEditing(d);
    setForm({
      title: d.title || '', externalLink: d.externalLink || '',
      category: d.category || 'General', pillar: d.pillar || '', comments: d.comments || ''
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) { setError('Title is required'); return; }
    try {
      setSaving(true); setError(null);
      const res = editing
        ? await authPut(`/api/reliability/documentation/${editing._id}`, form)
        : await authPost('/api/reliability/documentation', form);
      if (res.success) {
        setSuccess(editing ? 'Document updated.' : 'Document added.');
        setTimeout(() => setSuccess(null), 4000);
        setDialogOpen(false);
        loadData();
      } else {
        setError(res.message || 'Failed to save');
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await authDelete(`/api/reliability/documentation/${deleteTarget._id}`);
      setDeleteTarget(null);
      loadData();
    } catch (e) {
      setError(e.message);
    } finally {
      setDeleting(false);
    }
  };

  const filtered = docs.filter(d => !filterPillar || d.pillar === filterPillar);

  return (
    <Box>
      {/* Toolbar */}
      <Paper
        elevation={0}
        sx={{ p: 2, mb: 2.5, borderRadius: '16px', border: '1.5px solid rgba(0,0,0,0.08)' }}
      >
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <Typography variant="body1" fontWeight={600} color={PRIMARY}>Documentation</Typography>

          <FormControl size="small" sx={{ minWidth: 240, ...inputSx }}>
            <InputLabel>Pillar</InputLabel>
            <Select
              value={filterPillar}
              onChange={e => setFilterPillar(e.target.value)}
              label="Pillar"
            >
              {PILLAR_OPTIONS.map(p => <MenuItem key={p} value={p}>{p || 'All Pillars'}</MenuItem>)}
            </Select>
          </FormControl>

          <Typography variant="body2" color={MUTED} sx={{ mr: 'auto' }}>
            {filtered.length} record{filtered.length !== 1 ? 's' : ''}
          </Typography>

          {isAuthenticated() && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={openCreate}
              aria-label="Add a new document"
              sx={{
                textTransform: 'none', fontWeight: 600, borderRadius: '10px',
                bgcolor: PRIMARY, '&:hover': { bgcolor: PRIMARY_DARK }
              }}
            >
              Add Document
            </Button>
          )}
        </Box>
      </Paper>

      {success && <Alert severity="success" sx={{ mb: 2, borderRadius: '10px' }}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" p={6}>
          <CircularProgress sx={{ color: PRIMARY }} />
        </Box>
      ) : (
        <>
          {filtered.length === 0 && (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography color={MUTED} variant="body2">No documents found.</Typography>
            </Box>
          )}
          <Grid container spacing={2}>
            {filtered.map((d) => {
              const cc = CATEGORY_COLORS[d.category] || CATEGORY_COLORS.General;
              return (
                <Grid item xs={12} sm={6} lg={4} key={d._id}>
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: '16px',
                      border: '1.5px solid rgba(0,0,0,0.07)',
                      p: 2.5,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'box-shadow 0.2s',
                      '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }
                    }}
                  >
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Chip
                        label={d.category || 'General'}
                        size="small"
                        sx={{
                          bgcolor: cc.bg, color: cc.color,
                          fontWeight: 600, fontSize: '0.75rem', border: 'none',
                          borderRadius: '8px'
                        }}
                      />
                      {isAuthenticated() && (
                        <Box display="flex" gap={0.5}>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => openEdit(d)}
                              aria-label={`Edit document: ${d.title}`}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {isAdmin() && (
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setDeleteTarget(d)}
                                aria-label={`Delete document: ${d.title}`}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      )}
                    </Box>

                    <Typography variant="body1" fontWeight={600} color="#1a1a1a" mt={1}>
                      {d.title}
                    </Typography>

                    {d.pillar && (
                      <Chip
                        label={d.pillar}
                        size="small"
                        variant="outlined"
                        sx={{
                          mt: 1, alignSelf: 'flex-start',
                          fontSize: '0.72rem', borderColor: `${PRIMARY}40`, color: PRIMARY, borderRadius: '8px'
                        }}
                      />
                    )}

                    {d.comments && (
                      <Typography
                        variant="body2"
                        color={MUTED}
                        mt={1}
                        sx={{
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical'
                        }}
                      >
                        {d.comments}
                      </Typography>
                    )}

                    <Box sx={{ flex: 1 }} />

                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mt={2}
                      pt={1.5}
                      sx={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}
                    >
                      <Typography variant="caption" color={MUTED}>
                        {d.updatedAt ? format(new Date(d.updatedAt), 'dd MMM yyyy') : '—'}
                      </Typography>
                      {d.externalLink && (
                        <Button
                          variant="outlined"
                          size="small"
                          endIcon={<OpenInNewIcon sx={{ fontSize: '14px !important' }} />}
                          component="a"
                          href={d.externalLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`Open document: ${d.title}`}
                          sx={{
                            textTransform: 'none', fontWeight: 600,
                            borderRadius: '8px', fontSize: '0.75rem',
                            borderColor: `${PRIMARY}50`, color: PRIMARY,
                            '&:hover': { borderColor: PRIMARY, bgcolor: `${PRIMARY}06` }
                          }}
                        >
                          Open
                        </Button>
                      )}
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </>
      )}

      {/* Create/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '20px' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: PRIMARY, pb: 1 }}>
          {editing ? 'Edit Document' : 'Add Document'}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <TextField
            label="Title *"
            value={form.title}
            onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
            fullWidth size="small" sx={inputSx}
            autoFocus
          />
          <TextField
            label="External Link (URL)"
            value={form.externalLink}
            onChange={e => setForm(p => ({ ...p, externalLink: e.target.value }))}
            fullWidth size="small"
            placeholder="https://…"
            sx={inputSx}
          />
          <Box display="flex" gap={2}>
            <TextField
              label="Category"
              value={form.category}
              onChange={e => setForm(p => ({ ...p, category: e.target.value }))}
              fullWidth size="small" sx={inputSx}
              placeholder="General, RFC, ADR, Runbook…"
            />
            <FormControl size="small" fullWidth sx={inputSx}>
              <InputLabel>Pillar</InputLabel>
              <Select
                value={form.pillar}
                onChange={e => setForm(p => ({ ...p, pillar: e.target.value }))}
                label="Pillar"
              >
                {PILLAR_OPTIONS.map(p => <MenuItem key={p} value={p}>{p || 'None'}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
          <TextField
            label="Comments"
            value={form.comments}
            onChange={e => setForm(p => ({ ...p, comments: e.target.value }))}
            fullWidth size="small" multiline rows={3} sx={inputSx}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            onClick={() => setDialogOpen(false)}
            disabled={saving}
            sx={{ textTransform: 'none', borderRadius: '10px', color: MUTED }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{
              textTransform: 'none', fontWeight: 600, borderRadius: '10px',
              bgcolor: PRIMARY, '&:hover': { bgcolor: PRIMARY_DARK }
            }}
          >
            {saving ? 'Saving…' : editing ? 'Save Changes' : 'Add Document'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        PaperProps={{ sx: { borderRadius: '20px' } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Document</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>"{deleteTarget?.title}"</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            onClick={() => setDeleteTarget(null)}
            disabled={deleting}
            sx={{ textTransform: 'none', borderRadius: '10px', color: MUTED }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleting}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px' }}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReliabilityDocumentation;
